import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import seon from '@seontechnologies/seon-javascript-sdk';
import { useForm, Controller } from 'react-hook-form';
import { emailValidation, nameValidation, requiredValidation } from 'validators';
import { TextField, PasswordField } from 'seagull';
import GoogleButton from '~/components/GoogleButton/GoogleButton';
import IntuitButton from '~/components/IntuitButton/IntuitButton';
import { routes, UtmParams } from '~/constants';
import { useIoContext, useStoreActions } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useOnMount } from '~/hooks/useOnMount';
import { usePasswordStrength } from '~/hooks/usePasswordStrength';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { FormSeparatorVertical } from '~/pages/unAuthorizedPages/components/FormSeparator';
import { validatePassword } from '~/utils/password-policy';
import { FormContainer, FormTitle, FormFields, VerticalFormFields, HorizontalFormFields, SubmitButtonField, ErrorMessage, Footer, FooterText, FooterLink, FormSubTitle } from '../components/FormComponents';
import TermsAndPrivacyMessage from '../components/TermsAndPrivacyMessage';
import UnauthorizedPageContainer from '../components/UnauthorizedPageContainer';
import XeroSignInButton from '../components/XeroSignInButton/XeroSignInButton';
import { errorMessages } from '../constants';
import { LoginFlow } from '../LoginScreen/LoginScreen.types';
import OTPForm from '../LoginScreen/Steps/OTPForm';
import styles from './SignupScreen.module.scss';
export function SignupForm(props) {
    const { onSubmitted, visitorSyncFrame, visitorSyncOrigin } = props;
    const { t: texts } = useLocaleKeys('pages.auth.signup');
    const { logBI, api } = useStoreActions();
    const history = useHistory();
    const { reportFormSubmission, reportToHubspot, prepareFormObject } = useEventReporter();
    const [signupInProgress, setSignupInProgress] = useState(false);
    const [signupError, setSignupError] = useState(false);
    const [errorMessage, setErrorMessage] = useState();
    const getUtms = useUtmsParams();
    const { browser: { getOrigin } } = useIoContext();
    const { pathname } = useLocation();
    const { handleSubmit, control, formState, getValues, watch } = useForm({
        mode: 'onChange'
    });
    const { REFERRAL_DASHBOARD_UPGRADE } = useFeatureFlags();
    const passwordValue = watch('password', '');
    const userInputs = [
        getValues('email'),
        getValues('fullName.first'),
        getValues('fullName.last')
    ].filter(Boolean);
    const strengthScore = usePasswordStrength(passwordValue, userInputs);
    useOnMount(()=>{
        const utms = getUtms();
        if (UtmParams.Referral in utms) {
            logBI(18151, {
                referrer: utms[UtmParams.Referral]
            });
        }
    });
    const onSubmit = async (data)=>{
        const { fullName, email, password } = data;
        logBI(10206, {});
        setSignupInProgress(true);
        let clientContext;
        try {
            clientContext = await seon.getSession();
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'seon-signup'
                }
            });
        }
        const utms = getUtms();
        if (REFERRAL_DASHBOARD_UPGRADE && UtmParams.Referral in utms) {
            utms[UtmParams.ReferralV2] = utms[UtmParams.Referral];
            delete utms[UtmParams.Referral];
        }
        const signup = await handlePromise(api.auth.signup({
            fullName,
            email,
            password,
            utms,
            pageUrl: getOrigin() + pathname,
            clientContext
        }));
        if (!signup.success) {
            setSignupError(true);
            setSignupInProgress(false);
            const error = new ErrorResponse(signup.error);
            if (error.hasServerError(ServerError.EmailIsInvalid)) {
                setErrorMessage(errorMessages.noBusinessEmailError);
            } else if (error.hasServerError(ServerError.AccountAlreadyExists)) {
                setErrorMessage(/*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        "There is already a user with this email address.",
                        ' ',
                        /*#__PURE__*/ _jsx(FooterLink, {
                            toInternalLink: routes.login,
                            underline: "always",
                            testId: "try-logging-in-link",
                            children: "Try logging in instead"
                        })
                    ]
                }));
            } else {
                setErrorMessage(errorMessages.generalSignupError);
            }
        } else {
            reportFormSubmission('app-sign-up', [
                prepareFormObject('email', email)
            ]); //TODO - replace this with specific events
            reportToHubspot('app_sign_up_', {
                email,
                firstname: fullName.first,
                lastname: fullName.last
            });
            setSignupInProgress(false);
            onSubmitted(email);
            visitorSyncOrigin !== '' && (visitorSyncFrame === null || visitorSyncFrame === void 0 ? void 0 : visitorSyncFrame.postMessage({
                type: 'anc_tracker_request',
                action: 'sync_user',
                data: {
                    userId: signup.data.id,
                    properties: {},
                    origin: window.location.origin,
                    path: window.location.pathname
                }
            }, "https://".concat(visitorSyncOrigin)));
        }
    };
    return /*#__PURE__*/ _jsxs(FormContainer, {
        id: "signup-form",
        onSubmit: handleSubmit(onSubmit),
        "data-testid": "signup-form",
        onChange: ()=>setSignupError(false),
        children: [
            /*#__PURE__*/ _jsx(FormTitle, {
                children: texts.signUp()
            }),
            /*#__PURE__*/ _jsxs(FormSubTitle, {
                children: [
                    /*#__PURE__*/ _jsx(FooterText, {
                        children: " Already have an account? "
                    }),
                    /*#__PURE__*/ _jsx(FooterLink, {
                        toInternalLink: routes.login,
                        testId: "sign-in-link",
                        children: "Log in"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(FormFields, {
                children: /*#__PURE__*/ _jsxs(HorizontalFormFields, {
                    className: styles.formWrapper,
                    children: [
                        /*#__PURE__*/ _jsxs(VerticalFormFields, {
                            children: [
                                /*#__PURE__*/ _jsxs(HorizontalFormFields, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "fullName.first",
                                            defaultValue: "",
                                            rules: nameValidation('First name', {
                                                required: 'Please enter a first name'
                                            }),
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                    bi: {
                                                        name: 'signup_first_name'
                                                    },
                                                    collapseHelperText: false
                                                }, field), {
                                                    label: texts.firstNameLabel(),
                                                    error: fieldState.isTouched && !!fieldState.error,
                                                    helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                                    testId: "first-name-input"
                                                }));
                                            }
                                        }),
                                        /*#__PURE__*/ _jsx(Controller, {
                                            control: control,
                                            name: "fullName.last",
                                            defaultValue: "",
                                            rules: nameValidation('Last name', {
                                                required: 'Please enter a last name'
                                            }),
                                            render: (param)=>{
                                                let { field, fieldState } = param;
                                                var _fieldState_error;
                                                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                                    bi: {
                                                        name: 'signup_last_name'
                                                    },
                                                    collapseHelperText: false
                                                }, field), {
                                                    label: texts.lastNameLabel(),
                                                    error: fieldState.isTouched && !!fieldState.error,
                                                    helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                                    testId: "last-name-input"
                                                }));
                                            }
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "email",
                                    defaultValue: "",
                                    rules: emailValidation(undefined, {
                                        required: 'Please enter an email'
                                    }),
                                    render: (param)=>{
                                        let { field, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                            bi: {
                                                name: 'signup_email'
                                            },
                                            collapseHelperText: false
                                        }, field), {
                                            label: texts.emailLabel(),
                                            type: "email",
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            testId: "email-input"
                                        }));
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Controller, {
                                    control: control,
                                    name: "password",
                                    defaultValue: "",
                                    rules: _object_spread_props(_object_spread({}, requiredValidation('Please enter a password')), {
                                        validate: (value)=>validatePassword(value, [
                                                getValues('email'),
                                                getValues('fullName.first'),
                                                getValues('fullName.last')
                                            ].filter(Boolean))
                                    }),
                                    render: (param)=>{
                                        let { field, fieldState } = param;
                                        var _fieldState_error;
                                        return /*#__PURE__*/ _jsx(PasswordField, _object_spread_props(_object_spread({
                                            bi: {
                                                name: 'signup_password'
                                            }
                                        }, field), {
                                            onBlur: ()=>{
                                                logBI(10203, {
                                                    isPasswordValid: !fieldState.error
                                                });
                                                field.onBlur();
                                            },
                                            error: fieldState.isTouched && !!fieldState.error,
                                            helperText: fieldState.isTouched && ((_fieldState_error = fieldState.error) === null || _fieldState_error === void 0 ? void 0 : _fieldState_error.message),
                                            showStrengthMeter: true,
                                            strengthScore: strengthScore,
                                            testId: "password-input"
                                        }));
                                    }
                                }),
                                /*#__PURE__*/ _jsx(SubmitButtonField, {
                                    disabled: !formState.isValid,
                                    isLoading: signupInProgress,
                                    testId: "signup-button",
                                    children: "Continue"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(FormSeparatorVertical, {
                            children: "Or"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.oauthButtonsContainer,
                            children: [
                                /*#__PURE__*/ _jsx(GoogleButton, {
                                    testId: "google-btn-sign-up",
                                    onAuthError: (msg)=>{
                                        setErrorMessage(msg || errorMessages.generalSignupError);
                                        setSignupError(true);
                                    },
                                    onAuthSuccess: ()=>{
                                        history.push(routes.home);
                                    }
                                }),
                                /*#__PURE__*/ _jsx(XeroSignInButton, {
                                    mode: "signUp"
                                }),
                                /*#__PURE__*/ _jsx(IntuitButton, {
                                    mode: "signUp"
                                })
                            ]
                        })
                    ]
                })
            }),
            signupError && /*#__PURE__*/ _jsx(ErrorMessage, {
                testId: "error-msg",
                children: errorMessage
            }),
            /*#__PURE__*/ _jsx(Footer, {
                children: /*#__PURE__*/ _jsx(TermsAndPrivacyMessage, {
                    type: "signup"
                })
            })
        ]
    });
}
export function SignupScreen() {
    const [email, setEmail] = useState('');
    const [step, setStep] = useState('signup');
    const visitorSyncFrame = useMemo(()=>{
        var _document_getElementById;
        return (_document_getElementById = document.getElementById('visitor-sync-frame')) === null || _document_getElementById === void 0 ? void 0 : _document_getElementById.contentWindow;
    }, []);
    useSetDocumentTitle('Anchor Signup');
    return /*#__PURE__*/ _jsxs(UnauthorizedPageContainer, {
        testId: step,
        wideLayout: step === 'signup',
        otpLayout: step === 'waiting-verification',
        children: [
            step === 'signup' && /*#__PURE__*/ _jsx(SignupForm, {
                onSubmitted: (email)=>{
                    if (email) {
                        setEmail(email);
                        setStep('waiting-verification');
                    }
                },
                visitorSyncFrame: visitorSyncFrame,
                visitorSyncOrigin: window._visitorSyncHostname || ''
            }),
            step === 'waiting-verification' && /*#__PURE__*/ _jsx(OTPForm, {
                email: email,
                flow: LoginFlow.EmailVerification,
                onBack: ()=>setStep('signup'),
                shouldRequestOTP: false
            })
        ]
    });
}
